import React, { useState } from 'react';
import { connect } from 'react-redux';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import Waypoint from 'react-waypoint';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { useQuery } from 'react-query';

import * as analytics from '@eb/site-analytics';
import { collections as collectionsDuck } from '@eb/destination-utils';

import { trackLocationChange as trackLocationChangeAction } from '../actions/location';
import { trackEventClickInternal as trackEventClickInternalAction } from '../actions/analytics';
import { saveEvent as saveEventAction } from '../actions/event';
import { fetchEventExpansions } from '../api/expansions';
import { updateEventsWithExpansions as updateEventsWithExpansionsAction } from '../actions/expansions';
import { getBucketContent, getBrowseSection } from '../utils';
import { getOrganizersFromEvents } from '../selectors/organizers';
import { CollectionsBucket } from '../../components/FeaturedCollections';
import {
    FEATURED_TEMPLATE,
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    CARD_BIG,
    CALENDAR_TEMPLATE,
    COLLECTION_TEMPLATE,
    MAP_TEMPLATE,
    CATEGORY_TEMPLATE,
    NEARBY_CITIES_TEMPLATE,
    TRENDING_SEARCHES_TEMPLATE,
    SHARE_OPTIONS_PROPTYPE,
    ARTICLE_TEMPLATE,
    ORGANIZER_FOLLOW_TEMPLATE,
    ENTITY_TYPE_MAP,
    GA_CITY_BROWSE_CATEGORY,
} from '../constants';

const CardBucket = loadable(() =>
    import('../components/cardBucket/CardBucket'),
);
const MapBucket = loadable(() => import('../components/MapBucket'));
const CalendarBucket = loadable(() => import('../components/CalendarBucket'));
const CategoryBucket = loadable(() => import('../components/CategoryBucket'));
const OrganizerFollowBucket = loadable(() =>
    import('../components/organizerFollowBucket/OrganizerFollowBucket'),
);
const NearbyCitiesBucket = loadable(() =>
    import('../components/NearbyCitiesBucket'),
);
const TrendingSearchesBucket = loadable(() =>
    import('../components/TrendingSearchesBucket'),
);

const {
    actions: {
        addOrRemoveEventToCollection: addOrRemoveEventToCollectionAction,
        createCollection: createCollectionAction,
    },
    selectors: { getFormattedCollections },
} = collectionsDuck;

const { trackEventFromState, trackEventFromStateOnPageUnload } = analytics;

export const BUCKET_LOOKUP_MAP = {
    [FEATURED_TEMPLATE]: CardBucket,
    [LARGE_CARD_TEMPLATE_3]: CardBucket,
    [LARGE_CARD_TEMPLATE_6]: CardBucket,
    [CARD_TEMPLATE_4]: CardBucket,
    [CARD_TEMPLATE_8]: CardBucket,
    [CARD_BIG]: CardBucket,
    [CALENDAR_TEMPLATE]: CalendarBucket,
    [MAP_TEMPLATE]: MapBucket,
    [CATEGORY_TEMPLATE]: CategoryBucket,
    [NEARBY_CITIES_TEMPLATE]: NearbyCitiesBucket,
    [ARTICLE_TEMPLATE]: CardBucket,
    [TRENDING_SEARCHES_TEMPLATE]: TrendingSearchesBucket,
    [COLLECTION_TEMPLATE]: CollectionsBucket,
    [ORGANIZER_FOLLOW_TEMPLATE]: OrganizerFollowBucket,
};

const DecorativeImage = ({ decorationPlacement, decorativeImage }) => {
    let component = null;

    if (decorativeImage) {
        const classes = classNames(
            'decorative-image',
            `decorative-image--${decorationPlacement.position}`,
            decorationPlacement.classes,
        );
        const parentClasses = classNames(
            'decorative-image--container',
            decorationPlacement.parentClasses,
        );

        component = (
            <div className={parentClasses}>
                <img className={classes} src={decorativeImage} alt="" />
            </div>
        );
    }

    return component;
};

const removeImageUrl = (event) => ({
    ...event,
    imageUrl: undefined,
});

const shouldReturnEmpty = (content, type, template) => {
    /*Future proofing such that if api adds additional buckets app doesn't break*/
    /*
     If content is undefined, that means the bucket type map has the appropriate key,
     but the value associated to it necessary to access the actual content in the response
     has changed. Solution is we should send an error, but not let the app fall over.
    */
    if (type !== 'follow_organizer' && (!content || isEmpty(content))) {
        return true;
    }

    /* If the passed in type is not known in the ENTITY_TYPE_MAP this means that the API has
    started to return a layout format that is not known. The API should be allowed to progress
    without causing errors so no error should be fired. */
    if (!has(ENTITY_TYPE_MAP, type)) {
        return true;
    }

    /* If the passed in template is not known in the BUCKET_LOOKUP_MAP this means that the API has
    started to return a layout format that is not known. The API should be allowed to progress
    without causing errors so no error should be fired. */
    if (!has(BUCKET_LOOKUP_MAP, template)) {
        return true;
    }

    return false;
};

const EmptyBucket = (props) => {
    return <div key={props.type} className="empty-bucket" />;
};

/**
 * Hook which given a list of event IDs, runs
 * a call to search service to get the expanded versions
 *
 * @param {object} props
 */
const useEventExpansions = (props) => {
    const bucketContentIds = props.content
        ? props.content.map(({ id }) => id)
        : [];

    return useQuery(
        ['expandEvents', bucketContentIds],
        async () => {
            const response = await fetchEventExpansions(bucketContentIds);
            props.updateEventsWithExpansions(response);
        },
        { enabled: props.isVisible && props.type === 'event' },
    );
};

export const BrowseBucket = (props) => {
    const [isVisible, setVisible] = useState(false);
    const { url: linkUrl } = props.seeMore || {};

    if (shouldReturnEmpty(props.content, props.type, props.template)) {
        return <EmptyBucket type={props.type} />;
    }

    //Expand events in this bucket to get additional
    //data that we don't parse on the server
    useEventExpansions({ ...props, isVisible });

    let DynamicBucket = BUCKET_LOOKUP_MAP[props.template];
    let updatedContent = props.content;

    if (!isVisible && props.type !== 'follow_organizer') {
        updatedContent = props.content.map(removeImageUrl);
    }

    return (
        <Waypoint onEnter={() => setVisible(true)}>
            <div className={props.containerClasses} key={props.bucketKey}>
                <div className={props.bucketWrapperClasses}>
                    <DecorativeImage
                        decorationPlacement={props.decorationPlacement}
                        decorativeImage={props.decorativeImage}
                    />
                    <DynamicBucket
                        userId={props.userId}
                        gaCategory={props.gaCategory}
                        gaActionSuffix={props.gaActionSuffix}
                        gaSettings={props.gaSettings}
                        organizers={props.organizers}
                        currentPlace={props.currentPlace}
                        content={updatedContent}
                        name={props.name}
                        type={props.type}
                        template={props.template}
                        linkUrl={linkUrl}
                        onSaveEvent={(eventId, shouldSave) =>
                            props.onSaveEvent(eventId, shouldSave, true)
                        }
                        shareOptions={props.shareOptions}
                        isAuthenticated={props.isAuthenticated}
                        trackLocationChange={props.trackLocationChange}
                        trackGAEvent={props.trackGAEvent}
                        trackGAEventOnPageUnload={
                            props.trackGAEventOnPageUnload
                        }
                        onTrackEventClickInternal={(data) =>
                            props.trackEventClickInternal(data)
                        }
                        bucketKey={props.bucketKey}
                        allEventsUrl={props.allEventsUrl}
                        httpReferer={props.httpReferer}
                        locationSlug={props.locationSlug}
                        experiments={props.experiments}
                        featureFlags={props.featureFlags}
                        userCollections={props.userCollections}
                        onAddOrRemoveEventToCollection={
                            props.onAddOrRemoveEventToCollection
                        }
                        onCreateCollection={props.onCreateCollection}
                        seeMoreMarkedNoFollow={props.seeMoreMarkedNoFollow}
                        isGDPRCountry={props.isGDPRCountry}
                        isBotRequest={props.isBotRequest}
                    />
                </div>
            </div>
        </Waypoint>
    );
};

BrowseBucket.propTypes = {
    allEventsUrl: PropTypes.string,
    bucketKey: PropTypes.string,
    bucketWrapperClasses: PropTypes.string,
    currentPlace: PropTypes.string,
    containerClasses: PropTypes.string,
    //eslint-disable-next-line
    content: PropTypes.arrayOf(PropTypes.object),
    //eslint-disable-next-line
    decorationPlacement: PropTypes.object,
    decorativeImage: PropTypes.string,
    httpReferer: PropTypes.string,
    isAuthenticated: PropTypes.bool,
    linkUrl: PropTypes.string,
    locationSlug: PropTypes.string,
    name: PropTypes.string,
    onSaveEvent: PropTypes.func,
    onTrackEventClickInternal: PropTypes.func,
    shareOptions: SHARE_OPTIONS_PROPTYPE,
    template: PropTypes.string,
    trackLocationChange: PropTypes.func,
    trackGAEvent: PropTypes.func,
    trackGAEventOnPageUnload: PropTypes.func,
    type: PropTypes.string,
    userCollections: PropTypes.arrayOf(PropTypes.object),
    onAddOrRemoveEventToCollection: PropTypes.func,
    onCreateCollection: PropTypes.func,
    seeMoreMarkedNoFollow: PropTypes.bool,
    isGDPRCountry: PropTypes.bool,
    featureFlags: PropTypes.object,
    isBotRequest: PropTypes.bool,
};

export const _mapStateToProps = (state, ownProps) => {
    const {
        bucketConfig,
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
    } = ownProps;

    const section = getBrowseSection(state.location.isOnline);
    const content = getBucketContent(
        state.content,
        bucketConfig,
        state.currencyFormat,
        section,
    );
    const organizers =
        bucketConfig.type === 'follow_organizer'
            ? getOrganizersFromEvents(state)
            : undefined;

    return {
        content,
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
        organizers,
        userCollections: getFormattedCollections(state),
        currentPlace: state.location.currentPlace,
        currentPlaceParent: state.location.currentPlaceParent,
        placeType: state.location.placeType,
        isAuthenticated: state.auth.isAuthenticated,
        shareOptions: state.share.options,
        allEventsUrl: state.content.allEventsUrl,
        httpReferer: state.app.httpReferer,
        locationSlug: state.location.slug,
        domain: state.app.domain,
        featureFlags: state.app.featureFlags,
        experiments: state.app.experiments,
        userEmail: state.app.userEmail,
        publicId: state.app.publicId,
        userId: state.app.publicId,
        isBotRequest: state.app.isBotRequest,
        isGDPRCountry: state.app.isGDPRCountry,
        gaCategory: GA_CITY_BROWSE_CATEGORY,
        gaActionSuffix: 'CityBrowse',
        gaSettings: state.app.gaSettings,
        bucketKey: bucketConfig.key,
        trackGAEvent: (action) =>
            trackEventFromState(state, {
                category: GA_CITY_BROWSE_CATEGORY,
                ...action,
            }),
        trackGAEventOnPageUnload: (action) =>
            trackEventFromStateOnPageUnload(state, {
                category: GA_CITY_BROWSE_CATEGORY,
                ...action,
            }),
        ...bucketConfig,
    };
};
const _mapDispatchToProps = (dispatch) => ({
    onSaveEvent: (...args) => dispatch(saveEventAction(...args)),
    trackLocationChange: (...args) =>
        dispatch(trackLocationChangeAction(...args)),
    onAddOrRemoveEventToCollection: (...args) =>
        dispatch(
            addOrRemoveEventToCollectionAction(GA_CITY_BROWSE_CATEGORY)(
                ...args,
            ),
        ),
    onCreateCollection: (...args) =>
        dispatch(createCollectionAction(GA_CITY_BROWSE_CATEGORY)(...args)),
    trackEventClickInternal: (...args) =>
        dispatch(trackEventClickInternalAction(...args)),
    updateEventsWithExpansions: (...args) =>
        dispatch(updateEventsWithExpansionsAction(...args)),
});

export default connect(_mapStateToProps, _mapDispatchToProps)(BrowseBucket);
